import React from 'react';

/**
 * GoldenStartupWelcome - Welcome page for Golden Startup (1 Million Startups in 12 Months)
 *
 * Light color palette to distinguish from active learning environment
 * Explains capacity for application to livelihood, income sovereignty
 */
function GoldenStartupWelcome({ onBackToSIFISO }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F5F5F0',
      color: '#2A2A2A'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '50px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN STARTUP
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#6B6B6B'
              }}>
                1 Million Startups in 12 Months
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '20px'
          }} />

          <p style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#2A2A2A',
            marginBottom: '15px'
          }}>
            Application to Livelihood
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#6B6B6B',
            lineHeight: '1.7',
            maxWidth: '700px'
          }}>
            The capacity to turn what you know into value, income, and real-world problem solving.
            Livelihood literacy, not entrepreneurship training.
          </p>
        </header>

        {/* Main Content */}
        <main>
          {/* Status Box */}
          <div style={{
            backgroundColor: '#FFFBF0',
            border: '2px solid #D4AF37',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '40px'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#D4AF37',
              marginBottom: '15px'
            }}>
              Currently In Development
            </div>
            <p style={{
              fontSize: '1.05rem',
              color: '#2A2A2A',
              lineHeight: '1.7',
              marginBottom: '15px'
            }}>
              Golden Startup is being designed to help a million people turn capacity into income
              sovereignty. Not because startups are the goal—because the ability to sustain yourself
              through your own capacity is.
            </p>
            <p style={{
              fontSize: '0.95rem',
              color: '#6B6B6B',
              lineHeight: '1.7'
            }}>
              This is not entrepreneurship training. This is not pitch decks and venture capital.
              This is livelihood literacy—the ability to map what you can do to what people need,
              and navigate economic systems with dignity.
            </p>
          </div>

          {/* What This Builds */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What This Capacity Builds
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Capacity mapping (what you can do vs. what markets need)',
                  'Economic navigation (understanding how value flows)',
                  'Income sovereignty (sustaining yourself through your own ability)',
                  'Problem identification (seeing what people actually struggle with)',
                  'Dignity in exchange (offering value without exploitation)'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '15px' : '0',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    color: '#2A2A2A'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.3rem',
                      fontWeight: '700'
                    }}>
                      •
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Position in Loop */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              Position in the Loop
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden Startup is where capacity meets reality. It requires maturity across at
                least two other fingers—you must have built capacity and tested it before you
                can apply it sustainably.
              </p>
              <div style={{
                backgroundColor: '#F5F5F0',
                padding: '20px',
                borderRadius: '8px',
                borderLeft: '4px solid #D4AF37'
              }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#2A2A2A',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  "Golden Startup is not the immediate next step after Golden Learn. It is the
                  application phase—where you take what you've built, tested, and understood,
                  and turn it into something that sustains you and solves real problems."
                </p>
              </div>
            </div>
          </section>

          {/* Why "1 Million Startups" */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              Why "1 Million Startups in 12 Months"
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                This is not about building unicorns or raising venture capital.
              </p>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                It's about a million people who can:
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Sustain themselves through their own capacity',
                  'Solve real problems for real people',
                  'Navigate markets without losing dignity',
                  'Build income that doesn\'t depend on employment gatekeepers',
                  'Create value that circulates in their communities'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      →
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* What's Coming */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What's Being Designed
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden Startup will include:
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Capacity-to-market mapping exercises',
                  'Problem identification frameworks (what do people struggle with?)',
                  'Value exchange principles (dignified pricing, no exploitation)',
                  'Economic navigation (understanding money flow, taxes, sustainability)',
                  'Real experiments (start small, learn from reality, iterate)'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      →
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Principle */}
          <section>
            <div style={{
              backgroundColor: '#2A2A2A',
              color: '#F5F5F0',
              borderRadius: '10px',
              padding: '30px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                lineHeight: '1.7',
                margin: 0
              }}>
                Income sovereignty through capacity, not credentials.
                <br />
                <span style={{ color: '#D4AF37', fontStyle: 'italic' }}>
                  Value that sustains, work that dignifies.
                </span>
              </p>
            </div>
          </section>
        </main>

        {/* Footer Note */}
        <footer style={{
          marginTop: '60px',
          paddingTop: '30px',
          borderTop: '1px solid #E5E5D5',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#6B6B6B',
            lineHeight: '1.6'
          }}>
            Golden Startup will open when it's ready—designed with care, not rushed to launch.
            <br />
            Return to this page to check its status.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenStartupWelcome;
